import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from "./contexts/LanguageContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import SkipToContent from "./components/common/SkipToContent";
import CookieBanner from "./components/common/CookieBanner";
import Index from "./pages/Index";
import { trackPageView } from "./lib/analytics";
// Lazy-loaded pages for code splitting
import { lazy, Suspense, useEffect } from "react";

const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));

const RegisterStudent = lazy(() => import("./pages/RegisterStudent"));
const GoogleCallback = lazy(() => import("./pages/GoogleCallback"));
const RegisterTeacher = lazy(() => import("./pages/RegisterTeacher"));
const RegisterParent = lazy(() => import("./pages/RegisterParent"));
const ParentalConsentApprove = lazy(() => import("./pages/ParentalConsentApprove"));
const ParentalConsentPending = lazy(() => import("./pages/ParentalConsentPending"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ResendVerification = lazy(() => import("./pages/ResendVerification"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Settings = lazy(() => import("./pages/Settings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Teachers = lazy(() => import("./pages/Teachers"));
const Courses = lazy(() => import("./pages/Courses"));
const QuranForKids = lazy(() => import("./pages/QuranForKids"));
const SpecialtyLanding = lazy(() => import("./pages/SpecialtyLanding"));
const Glossary = lazy(() => import("./pages/Glossary"));
import { LANDING_PATHS } from "@/content/landings";
const About = lazy(() => import("./pages/About"));
const StudentPortal = lazy(() => import("./pages/student/StudentPortal"));
const TeacherPortal = lazy(() => import("./pages/teacher/TeacherPortal"));
const AdminPortal = lazy(() => import("./pages/admin/AdminPortal"));
const CMSPage = lazy(() => import("./pages/CMSPage"));

// Sprint F10: Detail pages (lazy loaded)
const TeacherProfile = lazy(() => import("./pages/TeacherProfile"));
const CourseDetail = lazy(() => import("./pages/CourseDetail"));

// Sprint F8: Advanced feature pages (lazy loaded)
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const GroupSessions = lazy(() => import("./pages/GroupSessions"));
const Scholarships = lazy(() => import("./pages/Scholarships"));
const FAQ = lazy(() => import("./pages/FAQ"));
const FamilyDashboard = lazy(() => import("./pages/FamilyDashboard"));
const FamilyPayments = lazy(() => import("./pages/FamilyPayments"));

// Sprint F11: Missing pages (lazy loaded)
const MediaLibrary = lazy(() => import("./pages/MediaLibrary"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const CompareTeachers = lazy(() => import("./pages/CompareTeachers"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

// Fires a GA4 page_view on every route change (no-op until VITE_GA_ID is set
// and the visitor grants analytics consent). Delayed a tick so Helmet has
// committed the new document.title before it's read.
const PageViewTracker = () => {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname + location.search;
    const timer = window.setTimeout(() => trackPageView(path), 200);
    return () => window.clearTimeout(timer);
  }, [location.pathname, location.search]);
  return null;
};

const LazyFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
  </div>
);

const App = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <LanguageProvider>
              <CurrencyProvider>
              <BrowserRouter>
                <TooltipProvider>
                  <SkipToContent />
                  <Toaster />
                  <Sonner />
                  <CookieBanner />
                  <PageViewTracker />
                  <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/login" element={<Suspense fallback={<LazyFallback />}><Login /></Suspense>} />
                  <Route path="/register" element={<Suspense fallback={<LazyFallback />}><Register /></Suspense>} />
                  <Route path="/register/student" element={<Suspense fallback={<LazyFallback />}><RegisterStudent /></Suspense>} />
                  <Route path="/register/teacher" element={<Suspense fallback={<LazyFallback />}><RegisterTeacher /></Suspense>} />
                  <Route path="/register/parent" element={<Suspense fallback={<LazyFallback />}><RegisterParent /></Suspense>} />
                  <Route path="/auth/google/callback" element={<Suspense fallback={<LazyFallback />}><GoogleCallback /></Suspense>} />
                  <Route path="/parental-consent/:token" element={<Suspense fallback={<LazyFallback />}><ParentalConsentApprove /></Suspense>} />
                  <Route path="/parental-consent-pending" element={<Suspense fallback={<LazyFallback />}><ParentalConsentPending /></Suspense>} />
                  <Route path="/forgot-password" element={<Suspense fallback={<LazyFallback />}><ForgotPassword /></Suspense>} />
                  <Route path="/verify-email" element={<Suspense fallback={<LazyFallback />}><VerifyEmail /></Suspense>} />
                  <Route path="/resend-verification" element={<Suspense fallback={<LazyFallback />}><ResendVerification /></Suspense>} />
                  <Route path="/update-password" element={<Suspense fallback={<LazyFallback />}><ResetPassword /></Suspense>} />
                  <Route path="/settings" element={<Suspense fallback={<LazyFallback />}><Settings /></Suspense>} />
                  <Route path="/teachers" element={<Suspense fallback={<LazyFallback />}><Teachers /></Suspense>} />
                  <Route path="/courses" element={<Suspense fallback={<LazyFallback />}><Courses /></Suspense>} />
                  <Route path="/courses/:slug" element={<Suspense fallback={<LazyFallback />}><CourseDetail /></Suspense>} />
                  <Route path="/quran-for-kids" element={<Suspense fallback={<LazyFallback />}><QuranForKids /></Suspense>} />
                  {/* Specialty landing pages — copy in src/content/landings.ts */}
                  {LANDING_PATHS.map((path) => (
                    <Route key={path} path={path} element={<Suspense fallback={<LazyFallback />}><SpecialtyLanding slug={path.slice(1)} /></Suspense>} />
                  ))}
                  <Route path="/glossary" element={<Suspense fallback={<LazyFallback />}><Glossary /></Suspense>} />
                  <Route path="/about" element={<Suspense fallback={<LazyFallback />}><About /></Suspense>} />

                  {/* Student Portal Routes */}
                  <Route path="/student/*" element={
                    <ProtectedRoute allowedRoles={['student', 'admin']}>
                      <Suspense fallback={<LazyFallback />}>
                        <StudentPortal />
                      </Suspense>
                    </ProtectedRoute>
                  } />

                  {/* Teacher Portal Routes - MUST be before /teacher/:id to avoid conflict */}
                  <Route path="/teacher/*" element={
                    <ProtectedRoute allowedRoles={['teacher', 'admin']}>
                      <Suspense fallback={<LazyFallback />}>
                        <TeacherPortal />
                      </Suspense>
                    </ProtectedRoute>
                  } />

                  {/* Public Teacher Profile - uses /teachers/:id to avoid conflict with /teacher/* portal routes */}
                  <Route path="/teachers/:id" element={<Suspense fallback={<LazyFallback />}><TeacherProfile /></Suspense>} />

                  {/* Admin Portal — single nested router with DashboardShell */}
                  <Route path="/admin/*" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <Suspense fallback={<LazyFallback />}>
                        <AdminPortal />
                      </Suspense>
                    </ProtectedRoute>
                  } />

                  {/* Media Library */}
                  <Route path="/media" element={<Suspense fallback={<LazyFallback />}><MediaLibrary /></Suspense>} />

                  {/* Sprint F11: New Public Routes */}
                  <Route path="/blog" element={<Suspense fallback={<LazyFallback />}><Blog /></Suspense>} />
                  <Route path="/blog/:slug" element={<Suspense fallback={<LazyFallback />}><BlogArticle /></Suspense>} />
                  <Route path="/compare-teachers" element={<Suspense fallback={<LazyFallback />}><CompareTeachers /></Suspense>} />
                  <Route path="/terms" element={<Suspense fallback={<LazyFallback />}><Terms /></Suspense>} />
                  <Route path="/privacy" element={<Suspense fallback={<LazyFallback />}><Privacy /></Suspense>} />

                  {/* Sprint F8: Public Feature Routes */}
                  <Route path="/leaderboard" element={
                    <Suspense fallback={<LazyFallback />}>
                      <Leaderboard />
                    </Suspense>
                  } />
                  <Route path="/group-sessions" element={
                    <Suspense fallback={<LazyFallback />}>
                      <GroupSessions />
                    </Suspense>
                  } />
                  <Route path="/scholarships" element={
                    <Suspense fallback={<LazyFallback />}>
                      <Scholarships />
                    </Suspense>
                  } />
                  <Route path="/faq" element={
                    <Suspense fallback={<LazyFallback />}>
                      <FAQ />
                    </Suspense>
                  } />
                  <Route path="/family" element={
                    <ProtectedRoute allowedRoles={['parent', 'student', 'admin']}>
                      <Suspense fallback={<LazyFallback />}>
                        <FamilyDashboard />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/family/payments" element={
                    <ProtectedRoute allowedRoles={['parent', 'student', 'admin']}>
                      <Suspense fallback={<LazyFallback />}>
                        <FamilyPayments />
                      </Suspense>
                    </ProtectedRoute>
                  } />

                  {/* Public CMS Page Route */}
                  <Route path="/page/:slug" element={<Suspense fallback={<LazyFallback />}><CMSPage /></Suspense>} />

                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<Suspense fallback={<LazyFallback />}><NotFound /></Suspense>} />
                  </Routes>
                </TooltipProvider>
              </BrowserRouter>
              </CurrencyProvider>
            </LanguageProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
